import {Dispatch} from 'redux'
import {trackTimetableEvent} from 'setup/redux/Actions/Timetable/timetable'

import {isSelectiveAiDomains} from '../HelperFunctions'

type CreditWallSubstep = 'credits_low' | 'credits_exhausted' | 'plan_limit_hit' | 'user_limit_hit'

/**
 * Tell the subscription service that the user hit a wall, so the lifecycle
 * emails that exist for it can fire — E14a for credits, E14b for the teacher
 * cap, E14c for the staff-user seat cap.
 *
 * The teacher cap and the user cap are separate substeps rather than one
 * `plan_limit_hit` carrying an `entity`, because they send different copy and
 * the engine picks a template by substep. They also dedupe independently, so a
 * school that hits both walls in one afternoon is not silenced on the second.
 *
 * This is needed because both walls can be raised entirely in the browser. The
 * generation pre-check compares the cached AI-credit balance and opens the
 * insufficient-credits modal WITHOUT calling /deduct/credits, and the staff-seat
 * cap disables the add button from the plan name alone. Neither reaches a
 * server, so without this the backend never learns the wall happened and the
 * user sees "you're out of credits" and then hears nothing.
 *
 * Fire-and-forget by construction: the promise resolves on failure as well as
 * success, so UI code can call it without awaiting and a tracking hiccup can
 * never stop the modal or toast from showing.
 *
 * Safe to call on EVERY blocked attempt — recordEvent collapses repeats for the
 * same tenant inside a 30-minute window server-side, so clicking a blocked
 * button five times still produces one email rather than five.
 *
 * SCOPE: selective AI domains only. AI credits, the credit wall and the DA1
 * lifecycle emails exist only for those tenants, so on any other domain this is
 * a no-op — the gate lives here rather than at each call site so a future caller
 * cannot leak these events onto a domain that has no such product.
 */
export const trackCreditWallEvent = (
  dispatch: Dispatch,
  urddId: string | number | null | undefined,
  substep: CreditWallSubstep,
  metadata?: Record<string, unknown>
): Promise<void> => {
  if (!isSelectiveAiDomains()) return Promise.resolve()

  return new Promise((resolve) => {
    dispatch(
      trackTimetableEvent(
        {
          urdd_id: urddId ? Number(urddId) : null,
          event_name: 'CreditsWall',
          substep,
          processing_time: null,
          metadata: metadata ?? null,
        },
        () => resolve(),
        () => resolve()
      ) as never
    )
  })
}
